import { Size, Image } from '~/lib/ApiModel';
import React from 'react';
import './SizeSelector.scss';
import { validMemoryOptions, validStorageOptions, validTransferOptions, GB_PER_TB } from '~/lib/helper/SizeHelper';
import { getOperatingSystemSurcharge } from '~/lib/helper/OperatingSystemHelper';
import { asNumberOrThrow } from '~/lib/helper/StringHelper';
import SelectableGrid from '~/components/SelectableGrid';
import LabelledProgress from '~/components/LabelledProgress';

type SizeSelectorProps = {
    sizes: Size[];
    disabled?: boolean;
    selectedOS?: Image;
    selectedSizeSlug?: string;
    selectedRegionSlug?: string;
    selectedMemory?: number;
    selectedStorage?: number;
    selectedTransfer?: number;
    currentSizeSlug?: string;
    currentMemory?: number;
    currentStorage?: number;
    currentTransfer?: number;
    selectedSizeTypeSlug?: string;
    onSizeSelect: (size: Size) => unknown;
    onSelectedMemoryChange: (memory_gb: number) => unknown;
    onSelectedStorageChange: (storage_gb: number) => unknown;
    onSelectedTransferChange: (transfer_gb: number) => unknown;
};

function memoryAndUnit(valueMb: number) {
    return valueMb < 2000 || valueMb % 1024 !== 0 ? `${valueMb} MB` : `${valueMb / 1024} GB`;
}

export default class SizeSelector extends React.Component<SizeSelectorProps> {
    getCost(size: Size, os?: Image, memory?: number, storage?: number, transfer?: number) {
        const totalMemory = memory ?? size.memory;
        const totalStorage = storage ?? size.disk;
        const totalTransfer = transfer ?? size.transfer;

        const extraMemoryCost = (totalMemory - size.memory) * size.options.memory_cost_per_additional_megabyte;
        const extraStorageCost = (totalStorage - size.disk) * size.options.disk_cost_per_additional_gigabyte;
        const extraTransferCost =
            (totalTransfer - size.transfer) * GB_PER_TB * size.options.transfer_cost_per_additional_gigabyte; // Transfer is in TB

        // To match old mpanel we don't include service level os surcharge discount here because it is selected after the plan
        const osCost = os
            ? getOperatingSystemSurcharge(os, size, {
                  memory: totalMemory,
                  disk: totalStorage,
                  transfer: totalTransfer,
              })
            : 0;
        return size.price_monthly + extraMemoryCost + extraStorageCost + extraTransferCost + osCost;
    }

    sizeUnavailable(size: Size): boolean {
        // A plan withdrawn from sale stays available to the server already on it.
        if (!size.available && size.slug !== this.props.currentSizeSlug) return true;
        return (
            !!this.props.selectedRegionSlug &&
            (size.regions_out_of_stock ?? []).indexOf(this.props.selectedRegionSlug) >= 0
        );
    }

    renderSizeRow(size: Size, groupName: string) {
        const cpuDescription = size.cpu_description ?? `${size.vcpus} ${size.vcpu_units}${size.vcpus > 1 ? 's' : ''}`;
        const isSelected = this.props.selectedSizeSlug === size.slug;
        const isUnavailable = this.sizeUnavailable(size);
        // Only the selected row renders the option <select>s, so skip computing
        // the (potentially large) valid-option lists for every other row.
        const isCurrentSize = size.slug === this.props.currentSizeSlug;
        const memoryOpts = isSelected
            ? validMemoryOptions(size, isCurrentSize ? this.props.currentMemory : undefined)
            : [];
        const storageOpts = isSelected
            ? validStorageOptions(size, isCurrentSize ? this.props.currentStorage : undefined)
            : [];
        const transferOpts = isSelected
            ? validTransferOptions(size, isCurrentSize ? this.props.currentTransfer : undefined)
            : [];

        return (
            <label
                className={`radio-row ${isSelected ? 'selected' : ''} ${isUnavailable ? 'disabled' : ''}`}
                key={size.slug}
            >
                <span className="cell cell-radio">
                    <input
                        type="radio"
                        name={groupName}
                        value={size.slug}
                        checked={isSelected}
                        disabled={this.props.disabled || isUnavailable}
                        onChange={() => this.props.onSizeSelect(size)}
                    />
                </span>
                <span
                    className="cell cell-title text-preline"
                    title={isUnavailable ? 'This plan is currently unavailable' : undefined}
                >
                    {cpuDescription}
                </span>
                <span className="cell">
                    <span className="th">Memory</span>
                    {isSelected && memoryOpts.length > 1 ? (
                        <select
                            aria-label="Memory"
                            disabled={this.props.disabled}
                            onChange={(e) => this.props.onSelectedMemoryChange(asNumberOrThrow(e.target.value))}
                            value={this.props.selectedMemory}
                        >
                            {memoryOpts.map((opt) => (
                                <option key={opt} value={opt}>
                                    {memoryAndUnit(opt)}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <span>{isSelected ? memoryAndUnit(memoryOpts[0] ?? 0) : `${size.memory / 1024} GB`}</span>
                    )}
                </span>
                <span className="cell">
                    <span className="th">Storage</span>
                    {isSelected && storageOpts.length > 1 ? (
                        <select
                            aria-label="Storage"
                            disabled={this.props.disabled}
                            onChange={(e) => this.props.onSelectedStorageChange(asNumberOrThrow(e.target.value))}
                            value={this.props.selectedStorage}
                        >
                            {storageOpts.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt} GB
                                </option>
                            ))}
                        </select>
                    ) : (
                        <span>{isSelected ? `${storageOpts[0]} GB` : `${size.disk} GB`}</span>
                    )}
                    {size.storage_description && <span className="ms-2 text-preline">{size.storage_description}</span>}
                </span>
                <span className="cell">
                    <span className="th">Transfer</span>
                    {isSelected && transferOpts.length > 1 ? (
                        <select
                            aria-label="Transfer"
                            disabled={this.props.disabled}
                            onChange={(e) => this.props.onSelectedTransferChange(asNumberOrThrow(e.target.value))}
                            value={this.props.selectedTransfer}
                        >
                            {transferOpts.map((opt) => (
                                <option key={opt} value={opt / GB_PER_TB}>
                                    {opt} GB
                                </option>
                            ))}
                        </select>
                    ) : (
                        <span>{isSelected ? `${transferOpts[0]} GB` : `${size.transfer * GB_PER_TB} GB`}</span>
                    )}
                </span>
                <span className="cell">
                    <span className="th">Price/Month</span>
                    <span>
                        $
                        {isSelected
                            ? this.getCost(
                                  size,
                                  this.props.selectedOS,
                                  this.props.selectedMemory,
                                  this.props.selectedStorage,
                                  this.props.selectedTransfer
                              ).toFixed(2)
                            : this.getCost(size, this.props.selectedOS).toFixed(2)}
                    </span>
                </span>
            </label>
        );
    }

    renderSizeRows(groupName: string) {
        const { sizes, currentSizeSlug, selectedSizeTypeSlug } = this.props;
        const currentSize = currentSizeSlug ? sizes.find((s) => s.slug === currentSizeSlug) : undefined;
        const isCurrentSizeDifferentType =
            currentSize && selectedSizeTypeSlug && currentSize.size_type.slug !== selectedSizeTypeSlug;

        if (!isCurrentSizeDifferentType) {
            return sizes.map((x) => this.renderSizeRow(x, groupName));
        }

        const otherSizes = sizes.filter((s) => s !== currentSize);
        return (
            <>
                {otherSizes.map((x) => this.renderSizeRow(x, groupName))}
                <div key="current-plan" className="current-plan">
                    Current Plan
                </div>
                {this.renderSizeRow(currentSize, groupName)}
            </>
        );
    }

    render() {
        const unavailableSizes = this.props.sizes.filter((s) => this.sizeUnavailable(s));
        return (
            <div className={`sizeSelector ${this.props.disabled ? 'disabled' : ''}`}>
                {this.props.disabled && <LabelledProgress animated value="100" label="Loading sizes" />}
                <SelectableGrid
                    legend="Server size"
                    gridClassName="sizeGrid"
                    observe={false}
                    header={
                        <>
                            <div></div>
                            <div>Processor</div>
                            <div>Memory</div>
                            <div>Storage</div>
                            <div>Transfer</div>
                            <div>Price</div>
                        </>
                    }
                >
                    {(groupName) => this.renderSizeRows(groupName)}
                </SelectableGrid>
                {unavailableSizes.length > 0 && (
                    <span>
                        <i className="icon ion-md-close-circle text-secondary" />
                        &nbsp;We currently do not have resources available to provision a server on{' '}
                        {unavailableSizes.length === 1 ? 'this plan.' : 'these plans.'}
                    </span>
                )}
            </div>
        );
    }
}
